// src/pages/sql/UpdateSnapRanking.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import "../../App.css";

function UpdateSnapRanking({ selectedRank, updatedRank, setUpdatedRank, setSelectedRank, setData }) {
  const [variants, setVariants] = useState([]);
  const [loadingVariants, setLoadingVariants] = useState(false);

  // 🟢 1. Always call useEffect, guard inside
  useEffect(() => {
    if (!selectedRank) return;
    setUpdatedRank({
      relianceScore: selectedRank.relianceScore ?? "",
      variantsScore: selectedRank.variantsScore ?? "",
      usabilityScore: selectedRank.usabilityScore ?? "",
      customHTML: selectedRank.customHTML ?? "",
      hidden: selectedRank.hidden ?? false,
    });
  }, [selectedRank, setUpdatedRank]);

  // 🟢 2. Always call useEffect, guard inside
  useEffect(() => {
    if (!selectedRank?.cardName) return;

    // === Normalize card name for SnapFan URLs ===
    const normalizeCardName = (name) => {
      if (!name) return "";
      let formatted = name
        .replace(/[^a-zA-Z0-9\s-]/g, "") // remove weird symbols
        .replace(/-/g, " ")
        .toLowerCase()
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join("");

      // Apply SnapFan-specific replacements
      formatted = formatted
        .replace("HawkeyeKateBishop", "KateBishop")
        .replace("JuggernautHorseman", "Juggernaut,HorsemanOfWar")
        .replace("SamWilsonCaptainAmerica", "SamWilson")
        .replace("DoctorDoom2099", "Doom2099")
        .replace("AranA", "Arana")
        .replace("MisterNegative", "MrNegative")
        .replace("OmegaSentinel", "KarimaShapandar")
        .replace("StarlordMasterOfTheSun", "StarLordMasterOfTheSun")
        .replace("ShouLaoTheUndying", "ShouLao")
        .replace("VivVision", "Viv")
        .replace("MisterFantastic", "MrFantastic")
        .replace("MODOK", "Modok")
        .replace("BatrocTheLeaper", "Batroc")
        .replace("RonanTheAccuser", "Ronan")
        .replace("TheHood", "Hood")
        .replace("Mbaku", "MBaku")
        .replace("TheLivingTribunal", "LivingTribunal")
        .replace("DoctorDoom", "DrDoom")
        .replace("PhoenixForce", "ThePhoenixForce")
        .replace("GorrTheGodButcher", "Gorr")
        .replace("TheInfinaut", "Infinaut")
        .replace("StarLord", "Starlord")
        .replace("MisterSinister", "MrSinister")
        .replace("TheFallenOne", "FallenOne")
        .replace("JaneFosterMightyThor", "JaneFoster")
        .replace("JoaquinTorresFalconIi", "JoaquinTorres")
        .replace("MisterFantasticFirstSteps", "MrFantasticFirstSteps")
        .replace("Modok", "M.o.d.o.k.")
        .replace("UatuTheWatcher", "Uatu");

      return formatted;
    };

    const fetchVariants = async () => {
      try {
        const formattedName = normalizeCardName(selectedRank.cardName);
        console.log("🔹 Normalized Card Name:", formattedName);

        setLoadingVariants(true);
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}api/proxy/snapfan/${encodeURIComponent(
            formattedName
          )}/variants`
        );

        console.log("Full SnapFan response:", res.data);
        console.log("Variants:", res.data?.variants);
        setVariants(res.data.variants || []);
      } catch (err) {
        console.error("Failed to fetch SnapFan variants:", err);
      } finally {
        setLoadingVariants(false);
      }
    };

    fetchVariants();
  }, [selectedRank]);

  // 🔸 now it's safe to conditionally render after all hooks
  if (!selectedRank) return null;

  // === Refresh rankings ===
  const fetchRankings = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}api/data/snapRankings/${selectedRank.userid}`
      );

      const mapped = res.data.map((r) => {
        const relianceScore = r.reliancescore;
        const variantsScore = r.variantsscore;
        const usabilityScore = r.usabilityscore;
        const avgScore = (relianceScore + variantsScore + usabilityScore) / 3;

        return {
          ...r,
          relianceScore,
          variantsScore,
          usabilityScore,
          cardName: r.cardname,
          cardHTML: r.cardhtml,
          cardDesc: r.carddesc,
          customHTML: r.customhtml,
          avgScore,
        };
      });

      mapped.sort((a, b) => b.avgScore - a.avgScore);
      setData(mapped);
    } catch (err) {
      console.error("Failed to refresh rankings:", err);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setUpdatedRank((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleUpdate = async () => {
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}api/updateSnapRanking/${selectedRank.id}`,
        {
          relianceScore: parseFloat(updatedRank.relianceScore),
          variantsScore: parseFloat(updatedRank.variantsScore),
          usabilityScore: parseFloat(updatedRank.usabilityScore),
          customHTML: updatedRank.customHTML?.trim() || null,
          hidden: updatedRank.hidden ?? false,
        }
      );
      fetchRankings();
      setSelectedRank(null);
    } catch (error) {
      console.error("Error updating snap ranking:", error);
    }
  };

  return (
    <div className="styled-info-modal">
      <h2 className="modal-title">{selectedRank.cardName}</h2>

      <input
        className="input"
        type="number"
        name="relianceScore"
        placeholder="Reliance"
        value={updatedRank.relianceScore ?? ""}
        onChange={handleChange}
      />
      <input
        className="input"
        type="number"
        name="variantsScore"
        placeholder="Variants"
        value={updatedRank.variantsScore ?? ""}
        onChange={handleChange}
      />
      <input
        className="input"
        type="number"
        name="usabilityScore"
        placeholder="Usability"
        value={updatedRank.usabilityScore ?? ""}
        onChange={handleChange}
      />

      {/* === Variant Image Selector === */}
      <div style={{ marginTop: "10px" }}>
        <h3 style={{ color: "rgb(197,176,55)", textAlign: "center" }}>
          Select Custom Variant
        </h3>

        {loadingVariants ? (
          <p style={{ textAlign: "center", color: "#aaa" }}>
            Loading variant images…
          </p>
        ) : variants.length === 0 ? (
          <p style={{ textAlign: "center", color: "#888" }}>
            No variants found for this card.
          </p>
        ) : (
          <>
            {updatedRank.customHTML && (
              <div style={{ textAlign: "center", margin: "10px 0" }}>
                <img
                  src={updatedRank.customHTML}
                  alt="Selected Variant"
                  style={{
                    maxWidth: "220px",
                    borderRadius: "8px",
                    boxShadow: "0 0 10px rgba(255, 215, 0, 0.3)",
                  }}
                />
              </div>
            )}

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
                gap: "10px",
                marginTop: "10px",
              }}
            >
              {variants.map((v, i) => (
                <div
                  key={i}
                  onClick={() =>
                    setUpdatedRank((prev) => ({
                      ...prev,
                      customHTML: v.src,
                    }))
                  }
                  style={{
                    border:
                      v.src === updatedRank.customHTML
                        ? "2px solid rgb(197,176,55)"
                        : "1px solid #444",
                    borderRadius: "8px",
                    padding: "4px",
                    cursor: "pointer",
                    background: "rgba(255,255,255,0.05)",
                    transition: "all 0.2s ease-in-out",
                  }}
                >
                  <img
                    src={v.src}
                    alt={v.artist || "Variant"}
                    style={{ width: "100%", borderRadius: "6px" }}
                  />
                  <p
                    style={{
                      textAlign: "center",
                      fontSize: "0.8em",
                      marginTop: "4px",
                      color: "#ccc",
                    }}
                  >
                    {v.artist || "Unknown"}
                  </p>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <button className="button" onClick={handleUpdate}>
        Update Ranking
      </button>
    </div>
  );
}

export default UpdateSnapRanking;
